import { Container, Graphics } from "pixi.js";
import { createFeatheredEllipse } from "../lib/soft-shape";
import type { Point, Token } from "../types";

function seedFrom(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  return (hash >>> 0) / 4294967296;
}

/** Animated top-down flame volume rendered as Pixi/WebGL geometry, with no logs or filter quad. */
export function createVolumetricFire(token: Token, position: Point, radius: number, alpha: number) {
  const fire = new Container(); fire.position.set(position.x, position.y); fire.alpha = alpha;
  const seed = seedFrom(token.id); const shadow = createFeatheredEllipse({ x: radius * 0.08, y: radius * 0.12, radiusX: radius * 1.22, radiusY: radius * 0.92, color: "#100805", alpha: 0.30, feather: 0.50 }); const glow = createFeatheredEllipse({ radiusX: radius * 1.52, radiusY: radius * 1.38, color: "#ff7022", alpha: 0.11, feather: 0.72, layers: 14 }); const flames = new Graphics(); const embers = new Graphics();
  fire.addChild(shadow, glow, flames, embers);

  let elapsed = seed * 9.0;
  const draw = () => {
    flames.clear(); embers.clear();
    flames.circle(0, 0, radius * (0.70 + Math.sin(elapsed * 4.1) * 0.035)).fill({ color: "#8f170b", alpha: 0.84 });
    for (let index = 0; index < 18; index += 1) {
      const phase = seed * 31 + index * 2.399; const angle = phase + Math.sin(elapsed * (0.7 + index % 4 * 0.11) + phase) * 0.28;
      const distance = radius * (0.12 + (index % 6) * 0.092); const pulse = 0.82 + Math.sin(elapsed * (3.2 + index % 5 * 0.37) + phase) * 0.18;
      const size = radius * (0.12 + (index % 4) * 0.035) * pulse;
      const color = index % 5 === 0 ? "#fff0a0" : index % 3 === 0 ? "#ffc340" : index % 2 ? "#ff7a1f" : "#d93112";
      flames.circle(Math.cos(angle) * distance, Math.sin(angle) * distance * 0.82, size).fill({ color, alpha: 0.90 });
    }
    flames.circle(-radius * 0.08, -radius * 0.04, radius * 0.25).fill({ color: "#fff4b0", alpha: 0.78 });
    for (let index = 0; index < 22; index += 1) {
      const life = (seed + index * 0.137 + elapsed * (0.16 + index % 5 * 0.018)) % 1; const angle = seed * 19 + index * 2.17 + elapsed * 0.28;
      const distance = radius * (0.22 + life * 1.02); const visibility = Math.sin(life * Math.PI);
      embers.circle(Math.cos(angle) * distance, Math.sin(angle) * distance * 0.78, Math.max(0.7, radius * 0.026 * visibility))
        .fill({ color: life < 0.52 ? "#fff09a" : "#ff8124", alpha: visibility * 0.84 });
    }
    glow.alpha = 0.70 + Math.sin(elapsed * 5.1 + seed * 8) * 0.14;
  };
  draw();
  return { fire, animate: (deltaMs: number) => { elapsed += Math.min(deltaMs, 80) / 1000; draw(); } };
}
